import React, { useEffect, useState } from "react";
import { CheckOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../api/http";
import userServiceApi from "../../api/userServiceApi";
import RecommendedIcon from "../../assets/images/icons/crown.svg";
import {
  AppFeaturesConstants,
  EssentialPlanColors,
  EssentialPlusPlanColors,
  PlanCurrency,
  PricePlan,
  StartedPlanColors,
  getPlanTabDisplayName,
} from "./constants";
import "./app-features-price-plan.scss";
import { Button, Modal, Switch } from "antd";
import Leftarrow from "../../assets/images/LeftArrow.svg";
import LocalizedPrice from "../common/LocalizedPrice";
import { useLocalizedCurrency } from "../../custom-hooks/useLocalizedCurrency";

const AppFeaturesPricePlan = ({
  showSubscriptionModal,
  setShowSubscriptionModal,
  setDidProSettingsUpdate,
}) => {
  const { micrositeId } = useParams();
  const navigate = useNavigate();

  const [startedData, setStartedData] = useState(null);
  const [essentialData, setEssentialData] = useState(null);
  const [essentialPlusData, setEssentialPlusData] = useState(null);
  const [plansData, setPlansData] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [currentSubscribed, setCurrentSubscribed] = useState("");
  const [planCurrency, setPlanCurrency] = useState(null);
  const [planeType, setPlaneType] = useState("yearly");
  const [cancelPlanLoading, setCancelPlanLoading] = useState(false);
  const [isCancelPlanModalOpen, setIsCancelPlanModalOpen] = useState(false);
  const [isFreePlanAlways, setIsFreePlanAlways] = useState(false);
  const [freePlanPeriod, setFreePlanPeriod] = useState(null);
  const connectedAccountId = sessionStorage.getItem("connectedAccountId");
  const localization = useLocalizedCurrency(planCurrency);
  const getPlanPrice = (planName, billingType) => {
    const cycle = billingType === "yearly" ? "YEARLY" : "MONTHLY";
    const planDetail = plansData?.find(
      (p) => p?.plan === planName && p?.subscriptionCycle === cycle,
    );
    const price = planDetail?.discountedPrice ?? planDetail?.actualPrice;
    if (price != null) return price;

    return billingType === "yearly"
      ? PricePlan.yearly[planName]
      : PricePlan.monthly[planName];
  };

  const sortTabsData = (list) => {
    return [...list].sort((a, b) => {
      const isAMandatory = a.tabType === "MANDATORY_TABS";
      const isBMandatory = b.tabType === "MANDATORY_TABS";

      // Mandatory tabs first
      if (isAMandatory && !isBMandatory) return -1;
      if (!isAMandatory && isBMandatory) return 1;

      // Alphabetical order inside each group
      return a?.displayName.localeCompare(b?.displayName);
    });
  };

  const handleFetchPlans = async () => {
    try {
      const response = await getRequest(
        userServiceApi.handleFetchPlans(micrositeId),
      );
      const data = response?.data;
      console.log("response====>", data);
      setDaysRemaining(data?.daysRemaining ?? 0);
      setIsFreePlanAlways(data?.isFreePlanAlways === true);
      setFreePlanPeriod(data?.freePlanPeriod);
      if (data?.currency) {
        setPlanCurrency(String(data.currency).trim().toLowerCase());
      }
      if (data?.planDetails?.length > 0) {
        const cycleForTabs = (planName) =>
          data.planDetails.find(
            (entry) =>
              entry?.plan === planName &&
              entry?.subscriptionCycle === "MONTHLY",
          ) ||
          data.planDetails.find((entry) => entry?.plan === planName);

        const starterPlan = cycleForTabs(AppFeaturesConstants.STARTER);
        const essentialsPlan = cycleForTabs(AppFeaturesConstants.ESSENTIALS);
        const essentialsPlusPlan = cycleForTabs(
          AppFeaturesConstants.ESSENTIALS_PLUS,
        );

        // Sorting tabs to show mandatory tabs at the top
        const order = ["STARTER", "ESSENTIALS", "ESSENTIALS_PLUS"];
        const sortedData = data?.planDetails?.sort(
          (a, b) => order?.indexOf(a.plan) - order?.indexOf(b.plan),
        );

        let subscribedPlan = "";
        sortedData?.forEach((dt) => {
          if (dt?.subscribed) {
            subscribedPlan = dt?.plan;
          }
        });

        setPlansData(data?.planDetails);
        setCurrentSubscribed(subscribedPlan);
        setStartedData({
          ...starterPlan,
          tabs: sortTabsData(starterPlan?.tabs),
        });
        setEssentialData({
          ...essentialsPlan,
          tabs: sortTabsData(essentialsPlan?.tabs),
        });
        setEssentialPlusData({
          ...essentialsPlusPlan,
          tabs: sortTabsData(essentialsPlusPlan?.tabs),
        });
      }
    } catch (error) {
      toast.error(error?.error);
    }
  };

  const getCheckoutAmount = (basePrice) =>
    localization.convert(basePrice, planCurrency);

  const handlePricePlan = async (planType, price) => {
    const payload = {
      connectedAccountId: connectedAccountId,
      micrositeId: micrositeId,
      newPlan: planType,
      currency: (
        localization.currency?.code ||
        planCurrency ||
        PlanCurrency.code
      ).toLowerCase(),
      amount: getCheckoutAmount(price),
      cycle: planeType === "yearly" ? "YEARLY" : "MONTHLY",
    };
    try {
      const response = await postRequest(
        userServiceApi.handleCheckoutSession(),
        payload,
      );
      if (response) {
        console.log("Response Price page ====> ", response);
        if (response?.url) {
          const partnerID = localStorage.getItem("partnerID");
          if (partnerID) {
            localStorage.setItem("partnerID", partnerID);
          }
          window.location.href = response?.url;
        }
      }
    } catch (error) {
      toast.error(error);
    }
  };

  const handleCloseCancelPlanModal = () => {
    setIsCancelPlanModalOpen(false);
  };

  const handleCancelPlan = async () => {
    const payload = {
      connectedAccountId: connectedAccountId,
      micrositeId: micrositeId,
    };
    try {
      setCancelPlanLoading(true);
      const response = await postRequest(
        userServiceApi.handleCancelSubscription(),
        payload,
      );
      if (response) {
        toast.success("Your plan has been cancelled successfully.");
        setIsCancelPlanModalOpen(false);
        setDidProSettingsUpdate?.(true);
        handleFetchPlans();
      }
    } catch (error) {
      toast.error(error?.error || error);
    } finally {
      setCancelPlanLoading(false);
    }
  };

  useEffect(() => {
    if (micrositeId) {
      handleFetchPlans();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micrositeId]);

  const firstLetterCapital = (value) => {
    return value?.replace(/^./, (str) => str?.toUpperCase());
  };

  const rawDaysRemaining = daysRemaining;
  const trialDaysRemaining =
    rawDaysRemaining != null && rawDaysRemaining !== ""
      ? Math.max(0, parseInt(rawDaysRemaining, 10) || 0)
      : null;
  const shouldShowUpgradeCta = isFreePlanAlways
    ? true
    : freePlanPeriod > 5
      ? false
      : trialDaysRemaining == null ||
        (trialDaysRemaining >= 0 && trialDaysRemaining <= 5);
  const getPlanSubscribed = (planName) => {
    const cycle = planeType === "yearly" ? "YEARLY" : "MONTHLY";
    const planDetail = plansData?.find(
      (p) => p?.plan === planName && p?.subscriptionCycle === cycle,
    );
    return planDetail?.subscribed === true;
  };
  const isPlanUpgradeCtaEnabled = (planSubscribed) =>
    isFreePlanAlways ? !planSubscribed : shouldShowUpgradeCta;
  const canCancelPlan = trialDaysRemaining >=14;

  const starterUpgradeEnabled = isPlanUpgradeCtaEnabled(
    getPlanSubscribed(AppFeaturesConstants.STARTER),
  );
  const essentialsUpgradeEnabled = isPlanUpgradeCtaEnabled(
    getPlanSubscribed(AppFeaturesConstants.ESSENTIALS),
  );
  const essentialsPlusUpgradeEnabled = isPlanUpgradeCtaEnabled(
    getPlanSubscribed(AppFeaturesConstants.ESSENTIALS_PLUS),
  );

  const billingSwitch = (
    <div className="price-plan-type">
      <div>Yearly</div>
      <Switch
        checked={planeType === "yearly" ? false : true}
        className={`active-plan-${planeType}`}
        onClick={(e) => {
          if (e) {
            setPlaneType("monthly");
          } else {
            setPlaneType("yearly");
          }
        }}
      />
      <div>Monthly</div>
    </div>
  );

  return (
    <>
      <div className="app-features-price-plan-container">
        <div className="app-features-management">
          <div className="price-plan-heading-wrap">
            <button
              type="button"
              className="price-plan-back-btn"
              onClick={() => navigate(micrositeId ? `/${micrositeId}` : "/")}
            >
              <img src={Leftarrow} alt="Back" />
            </button>
            <div className="app-features-management-header">
              <div className="header-title-row">
                <div className="header-title">
                  <strong>Choose Your Plan</strong>
                </div>
                {billingSwitch}
              </div>
              <div className="header-description">
                Scale your presence with the right feature set. Upgrade anytime.
              </div>
            </div>
            {/* <button
              type="button"
              className="price-plan-cancel-btn"
              disabled={!canCancelPlan || cancelPlanLoading}
              onClick={() => setIsCancelPlanModalOpen(true)}
            >
              Cancel
            </button> */}
          </div>

          <div className="discounted-price">
            <span>Enjoy More Features</span>
          </div>

          <div className="app-features-management-content">
            {/* Starter Plan  */}
            <div
              className="app-features-card-item"
              style={{
                border: `1px solid ${StartedPlanColors.border}`,
              }}
            >
              <div className="card-item-header">
                <div className="dot-wrapper">
                  <div className="dot"></div>
                </div>

                <h3>Starter</h3>
                <span className="card-item-subtitle">&nbsp;</span>
                <div className="card-item-price-plan">
                  <span>
                    <LocalizedPrice
                      amount={getPlanPrice(
                        AppFeaturesConstants.STARTER,
                        planeType,
                      )}
                      fromCurrency={planCurrency}
                      localization={localization}
                    />
                    /{firstLetterCapital(planeType)}
                  </span>
                </div>
              </div>

              <div className="card-item-content">
                {startedData?.tabs?.map((item) => {
                  return (
                    <>
                      <div
                        className="card-items-menu"
                        key={`starter-${item?.id}`}
                      >
                        {getPlanTabDisplayName(item?.displayName)}
                        <div className="card-item-check-icon">
                          <CheckOutlined />
                        </div>
                      </div>
                    </>
                  );
                })}
              </div>
              {shouldShowUpgradeCta && (
              <div className="card-btn">
                <button
                  className="card-item-button"
                  disabled={!starterUpgradeEnabled}
                  style={{
                    color: StartedPlanColors.btnText,
                    background: StartedPlanColors.btnBg,
                    border: `1px solid ${StartedPlanColors.border}`,
                    cursor: starterUpgradeEnabled ? "pointer" : "not-allowed",
                  }}
                  onClick={() =>
                    handlePricePlan(
                      AppFeaturesConstants.STARTER,
                      getPlanPrice(AppFeaturesConstants.STARTER, planeType),
                    )
                  }
                >
                  Upgrade to Starter
                </button>
              </div>
              )}

              {currentSubscribed === AppFeaturesConstants.STARTER && (
                <div
                  className="card-plan-badge"
                  style={{ background: StartedPlanColors.headerText }}
                >
                  Current Plan
                </div>
              )}
            </div>

            {/* Essential Plan  */}
            <div
              className="app-features-card-item"
              style={{
                border: `1px solid ${EssentialPlanColors.border}`,
              }}
            >
              <div className="card-item-header">
                <div className="dot-wrapper" style={{ background: "#DCFAE6" }}>
                  <div
                    className="dot"
                    style={{ background: EssentialPlanColors.btnBg }}
                  ></div>
                </div>

                <h3>Essentials</h3>
                <span className="card-item-subtitle">
                  Everything in Starter, Plus business tools
                </span>
                <div className="card-item-price-plan">
                  <span>
                    <LocalizedPrice
                      amount={getPlanPrice(
                        AppFeaturesConstants.ESSENTIALS,
                        planeType,
                      )}
                      fromCurrency={planCurrency}
                      localization={localization}
                    />
                    /{firstLetterCapital(planeType)}
                  </span>
                </div>
              </div>

              <div className="card-item-content">
                {essentialData?.tabs?.map((item) => {
                  return (
                    <>
                      <div
                        className="card-items-menu"
                        key={`essential-${item?.id}`}
                      >
                        {getPlanTabDisplayName(item?.displayName)}
                        <div className="card-item-check-icon">
                          <CheckOutlined />
                        </div>
                      </div>
                    </>
                  );
                })}
              </div>

              {shouldShowUpgradeCta && (
              <div className="card-btn">
                <button
                  className="card-item-button"
                  disabled={!essentialsUpgradeEnabled}
                  style={{
                    color: EssentialPlanColors.btnText,
                    background: EssentialPlanColors.btnBg,
                    border: `1px solid ${EssentialPlanColors.border}`,
                    cursor: essentialsUpgradeEnabled ? "pointer" : "not-allowed",
                  }}
                  onClick={() =>
                    handlePricePlan(
                      AppFeaturesConstants.ESSENTIALS,
                      getPlanPrice(AppFeaturesConstants.ESSENTIALS, planeType),
                    )
                  }
                >
                  Upgrade to Essentials
                </button>
              </div>
              )}

              {currentSubscribed === AppFeaturesConstants.ESSENTIALS && (
                <div
                  className="card-plan-badge"
                  style={{ background: EssentialPlanColors.headerText }}
                >
                  Current Plan
                </div>
              )}

              {currentSubscribed === AppFeaturesConstants.STARTER && (
                <div
                  className="card-plan-badge"
                  style={{
                    background: EssentialPlanColors.btnBg,
                  }}
                >
                  <img alt="Recommended" src={RecommendedIcon} /> Recommended
                </div>
              )}
            </div>

            {/* Essential Plus Plan*/}
            <div
              className="app-features-card-item"
              style={{
                border: `1px solid ${EssentialPlusPlanColors.btnBg}`,
              }}
            >
              <div className="card-item-header">
                <div className="dot-wrapper" style={{ background: "#EBE9FE" }}>
                  <div
                    className="dot"
                    style={{ background: EssentialPlusPlanColors.btnBg }}
                  ></div>
                </div>

                <h3>Essentials Plus</h3>
                <span className="card-item-subtitle">
                  Full suite with HR & PR features
                </span>
                <div className="card-item-price-plan">
                  <span>
                    <LocalizedPrice
                      amount={getPlanPrice(
                        AppFeaturesConstants.ESSENTIALS_PLUS,
                        planeType,
                      )}
                      fromCurrency={planCurrency}
                      localization={localization}
                    />
                    /{firstLetterCapital(planeType)}
                  </span>
                </div>
              </div>

              <div className="card-item-content">
                {essentialPlusData?.tabs?.map((item) => {
                  return (
                    <>
                      <div
                        className="card-items-menu"
                        key={`essential-plus-${item?.id}`}
                      >
                        {getPlanTabDisplayName(item?.displayName)}
                        <div className="card-item-check-icon">
                          <CheckOutlined />
                        </div>
                      </div>
                    </>
                  );
                })}
              </div>
              {shouldShowUpgradeCta && (
                  <div className="card-btn">
                    <button
                      className="card-item-button"
                      disabled={!essentialsPlusUpgradeEnabled}
                      style={{
                        color: EssentialPlusPlanColors.btnText,
                        background: EssentialPlusPlanColors.btnBg,
                        border: `1px solid ${EssentialPlusPlanColors.btnBg}`,
                        cursor: essentialsPlusUpgradeEnabled
                          ? "pointer"
                          : "not-allowed",
                      }}
                      onClick={() =>
                        handlePricePlan(
                          AppFeaturesConstants.ESSENTIALS_PLUS,
                          getPlanPrice(
                            AppFeaturesConstants.ESSENTIALS_PLUS,
                            planeType,
                          ),
                        )
                      }
                    >
                      Upgrade to Essentials Plus
                    </button>
                  </div>
                )}

              {currentSubscribed === AppFeaturesConstants.ESSENTIALS_PLUS && (
                <div
                  className="card-plan-badge"
                  style={{ background: EssentialPlusPlanColors.headerText }}
                >
                  Current Plan
                </div>
              )}

              {currentSubscribed === AppFeaturesConstants.ESSENTIALS && (
                <div
                  className="card-plan-badge"
                  style={{
                    background: EssentialPlusPlanColors.btnBg,
                  }}
                >
                  <img alt="Recommended" src={RecommendedIcon} /> Recommended
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        className="confirmation-modal"
        open={isCancelPlanModalOpen}
        footer={null}
        onCancel={handleCloseCancelPlanModal}
        destroyOnClose={true}
        closable={false}
      >
        <div className="message">
          Do you want to cancel the current plan?
        </div>
        <div className="modalBtns">
          <Button className="cancel-btn" onClick={handleCloseCancelPlanModal}>
            Cancel
          </Button>
          <Button
            className="confirm-btn"
            loading={cancelPlanLoading}
            onClick={handleCancelPlan}
          >
            Yes
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default AppFeaturesPricePlan;
